import { defineStore } from 'pinia';
import { UserAccount } from '@schema';
import {
  DEFAULT_DOMAIN_NAME,
  DOMAIN_USER_DOMAIN_ADMIN_ROLE,
  PROJECT_ADMIN_USER_ROLE,
  SYSTEM_ADMIN_PERMISSION,
  SYSTEM_SUPERUSER_PERMISSION
} from '@/common/constants/domains.constants';
import {
  OPENSTACK_AUTH_RECEIPT_HEADER,
  AUTH_RECEIPT_EXPIRATION_TIME
} from '@/common/constants/auth.constants';
import api from '@/common/utils/api';
import { isResponse } from '@/common/utils/predicates';
import { store } from '@/common/root';
import { isError, uniq, isPlainObject } from 'lodash-es';
import usePermissionsStore from '@/common/store/pinia/permissions';
import AccessDisabledError from '@/common/utils/errors/AccessDisabledError';
import TotpAuthorizationError from '@/common/utils/errors/TotpAuthorizationError';
import { findMutationsByName } from '@/common/utils/store.util';
import { resetAbility } from '@/common/ability';
import storeModulesGroups from '@/common/store/modules/storeModulesGroups';
import useThemeStore from '@/common/store/pinia/theme';
import { setPersonProperties, updateUser } from '@/common/telemetry';
import useInfoStore from '@/common/store/pinia/info';

export interface ProjectAuthResponse {
  token: string;
  roles: string[];
}

const getInitialState = () => ({
  accessDisabled: <'user' | 'project' | 'domain' | ''>'',
  currentProject: '',
  currentIdpId: null as string | null,
  currentUser: null as UserAccount | null,
  domain: '',
  pending: false,
  scopedToken: '',
  scopedRoles: [] as string[],
  sessionError: '',
  isSwitchingScope: false,
  storageAccessGranted: true,
  authReceiptTimestamp: null as number | null,
  authReceiptTimerId: undefined as ReturnType<typeof setTimeout> | undefined,
  authReceiptExpired: false
});

const ACCOUNTS_API_URL = '/api/v2/accounts';

export const useAuthStore = defineStore('auth', {
  state: () => getInitialState(),
  getters: {
    currentAdminProjectId: state => state.currentUser?.project_id || undefined, // scopedToken
    currentDomain: state => state.domain || DEFAULT_DOMAIN_NAME,
    currentUserProjectId: state => state.currentUser?.project_id || state.currentProject,
    domainMode(state): boolean {
      return !!(!state.currentProject && this.isDomainAdmin && state.currentUser?.domain_id);
    },
    getScopedToken: state => state.scopedToken || state.currentUser?.scoped_token,
    isDomainAdmin: state => !!state.currentUser?.roles?.find(it => it.id === DOMAIN_USER_DOMAIN_ADMIN_ROLE),
    isProjectAdmin: state => state.scopedRoles.includes(PROJECT_ADMIN_USER_ROLE),
    isSystemAdmin: state => !!state.currentUser?.roles?.find(it => it.id === SYSTEM_ADMIN_PERMISSION || it.id === SYSTEM_SUPERUSER_PERMISSION) || state.currentUser?.is_superuser,
    isLoggedIn: state => !!state.currentUser, // eslint-disable-line camelcase
    isLoggingIn: state => state.pending
  },
  actions: {
    async initAuthConfig() {
      const { data } = await this.getUserProjects();
      const currProjectId = this.currentProject || data.data.filter((it) => it.enabled)[0]?.id;
      this.setProject(currProjectId || '');
      this.scopedToken = currProjectId;
    },
    async changePassword(data) {
      return api.put(`${ACCOUNTS_API_URL}/change-password/`, data);
    },
    async checkAccessDisabled() {
      try {
        await this.getProfileInfo({ skipAuthGuard: true });
        const res = await this.getUserProjects({ unscopedRequest: true });
        const enabledProjects = res.data.data.filter(it => it.enabled);
        if (!enabledProjects.length && !this.isDomainAdmin) {
          this.accessDisabled = 'project';
        }
        return res;
      } catch (err) {
        if (isResponse(err)) {
          // If user is deleted
          if (err.response?.status === 404) {
            return this.signOut();
          }

          // If user is disabled
          if (err.response?.status === 403) {
            this.accessDisabled = 'user';
            return err.response;
          }
        }

        // If project is disabled
        return this.getUserProjects({ unscopedRequest: true, forceAuthGuard: true })
          .then(res => {
            this.accessDisabled = 'project';
            return res;
          });
      }
    },
    async doSignIn(action: () => any) {
      this.pending = true;
      const permissionsStore = usePermissionsStore();
      try {
        const { data: user } = await action();
        await Promise.all([
          store.dispatch('projects/FETCH_ITEMS'),
          permissionsStore.fetchAuthMeta()
        ]);
        this.successLogin(user);
        await store.dispatch('socket/CONNECT');
      } catch (err) {
        if (isError(err)) {
          this.sessionError = err.message;
        }

        if (isResponse(err)) {
          const totpReceipt = err.response.headers?.get(OPENSTACK_AUTH_RECEIPT_HEADER);

          if (totpReceipt) {
            this.startAuthReceiptTimer();
            throw new TotpAuthorizationError(err.response, (err as any).body);
          }
        }

        throw err;
      } finally {
        this.pending = false;
      }
    },
    async doSsSignIn(action: () => any, domain: string) {
      this.pending = true;

      try {
        const { data: user } = await action();
        this.setDomain(domain);
        const { data, response } = await this.getUserProjects();
        await this.processProject(data.data, user, response);
        this.fetchTheme(domain);
        this.successLogin(user);
        await store.dispatch('socket/CONNECT');
      } catch (err) {
        if (isError(err)) {
          this.sessionError = err.message;
        }

        if (isResponse(err)) {
          const totpReceipt = err.response.headers?.get(OPENSTACK_AUTH_RECEIPT_HEADER);

          if (totpReceipt) {
            this.startAuthReceiptTimer();
            throw new TotpAuthorizationError(err.response, (err as any).body);
          }

          throw new AccessDisabledError(err.response);
        }

        throw err;
      } finally {
        this.pending = false;
      }
    },
    async getProfileInfo(params: any = undefined) {
      return api.get<UserAccount>(`${ACCOUNTS_API_URL}/whoami/`, undefined, params);
    },
    async getUserProjects(params: any = undefined) {
      const res = await api.get<{ data: DomainProject[] }>(`${ACCOUNTS_API_URL}/projects`, undefined, params);
      await store.dispatch('projects/SET_ITEMS', { data: res.data });
      return res;
    },
    async processProject(projects: DomainProject[], user: UserAccount, response: Response) {
      const currProjectId = this.currentProject || projects.filter(it => it.enabled)[0]?.id;
      const permissionsStore = usePermissionsStore();

      if (currProjectId) {
        return this.signInProject(currProjectId);
      }
      this.setProject('');
      this.setToken('');

      const isDomainAdmin = !!user.roles?.find(role => role.id === DOMAIN_USER_DOMAIN_ADMIN_ROLE);

      if (isDomainAdmin) {
        return permissionsStore.fetchAuthMeta();
      }
      await this.signOut();
      throw new AccessDisabledError(response);
    },
    setCurrentUser(user: UserAccount) {
      this.currentUser = { ...this.currentUser, ...user };
      this.currentIdpId = this.currentUser.external_provider || null;
      localStorage.setItem('user', JSON.stringify(this.currentUser));
      localStorage.setItem('token', user.token);
    },
    setDomain(domain: string) {
      localStorage.setItem('domain', domain);
      this.domain = domain;
    },
    setProject(currentProject: string) {
      localStorage.setItem('currentProject', currentProject);
      this.currentProject = currentProject;
    },
    setToken(token: string) {
      localStorage.setItem('scopedToken', token);
      this.scopedToken = token;
    },
    setScopedRoles(roles: string[]) {
      localStorage.setItem('scopedRoles', JSON.stringify(roles));
      this.scopedRoles = roles;
    },
    async fetchTheme(domain: string) {
      const themeStore = useThemeStore();
      await themeStore.fetchTheme(domain);
    },
    async signIn(form) {
      return this.doSignIn(() => api.post('/api/v2/login', form));
    },
    async signInTotp(username: string, passcode: string, receipt?: string) {
      const headers = receipt ? { [OPENSTACK_AUTH_RECEIPT_HEADER]: receipt } : {};
      return this.doSignIn(() => api.post('/api/v2/login/totp', { username, passcode }, {
        headers
      }));
    },
    signInTotpWithDomain(domain: string, username: string, password: string, passcode: string, receipt?: string) {
      const headers = receipt ? { [OPENSTACK_AUTH_RECEIPT_HEADER]: receipt } : {};
      return this.doSsSignIn(
        () => api.post('/api/v2/login/totp',
          {
            domain,
            username,
            password,
            passcode
          },
          {
            headers,
            skipAuthGuard: false
          }),
        domain
      );
    },
    async signInGrafana() {
      return api.get('/grafana/login');
    },
    isTotpError(err: any): boolean {
      if (!isResponse(err)) {
        return false;
      }

      const data = (err as any).body;
      if (!isPlainObject(data)) {
        return false;
      }

      const errorMessage = data?.error?.message || data?.message || '';
      return typeof errorMessage === 'string' && errorMessage.toLowerCase().includes('totp');
    },
    async signInProject(projectId: string) {
      this.isSwitchingScope = true;
      const permissionsStore = usePermissionsStore();
      await store.dispatch('socket/DISCONNECT');
      const res = await this.signInProjectUnscopedUser(projectId);
      await store.dispatch('compute/FLUSH_COMPUTE');
      const scopedToken = res.data.token;
      this.setProject(projectId);
      this.setToken(scopedToken);
      this.setScopedRoles(res.data.roles);

      updateUser(this.currentUser, {
        isSystemAdmin: this.isSystemAdmin,
        isDomainAdmin: this.isDomainAdmin,
        isProjectAdmin: this.isProjectAdmin
      });

      setPersonProperties({
        vz_project_id: projectId,
      });

      await Promise.allSettled([
        store.dispatch('socket/CONNECT'),
        permissionsStore.fetchAuthMeta()
      ]);
      this.isSwitchingScope = false;
    },
    // Before editing project quotas as domain administrator in self-service, we need to sign in to that specific project
    // in order to properly do scoped request
    signInProjectUnscopedUser(projectId: string): Promise<FetchResponse<ProjectAuthResponse>> {
      return api.post<ProjectAuthResponse>(`${ACCOUNTS_API_URL}/projects/${projectId}/auth/`, {}, {
        unscopedRequest: true
      }).catch((err) => {
        if (isResponse(err)
          && err.response?.status === 401
          && this.isTotpError(err)) {
          this.signOut();
        }

        throw err;
      });
    },
    async signInToAdminViaIdp() {
      return this.doSignIn(() => this.getProfileInfo());
    },
    async signInToSSViaIdp(domain: string) {
      return this.doSsSignIn(() => this.getProfileInfo(), domain);
    },
    async signInWithDomain(form) {
      return this.doSsSignIn(
        () => api.post('/api/v2/login', form, { skipAuthGuard: false }),
        form.domain);
    },
    flushState() {
      const { currentIdpId, domain, storageAccessGranted, ...flushedState } = getInitialState();
      Object.assign(this.$state, flushedState);
    },
    resetIdpId() {
      const { currentIdpId } = getInitialState();
      this.currentIdpId = currentIdpId;
    },
    async signOut() {
      this.clearAuthReceiptTimer();
      this.authReceiptExpired = false;
      if (this.currentUser) {
        try {
          await api.post('/api/v2/logout');
          await store.dispatch('socket/DISCONNECT');
        } finally {
          localStorage.removeItem('user');
          localStorage.removeItem('scopedToken');
          localStorage.removeItem('scopedRoles');
          this.userSignedOut();
          this.flushState(); // todo: Replace with reset all registered pinia stores
        }
      }
    },
    successLogin(user: UserAccount) {
      this.clearAuthReceiptTimer();
      this.authReceiptExpired = false;
      this.sessionError = '';
      this.pending = false;
      this.setCurrentUser(user);

      updateUser(this.currentUser, {
        isSystemAdmin: this.isSystemAdmin,
        isDomainAdmin: this.isDomainAdmin,
        isProjectAdmin: this.isProjectAdmin
      });

      const infoStore = useInfoStore();
      infoStore.fetchAndRegisterLicenseData();

      return user;
    },
    setStorageAccessState(state: boolean) {
      this.storageAccessGranted = state;
    },
    async switchToDomainMode() {
      this.isSwitchingScope = true;
      const permissionsStore = usePermissionsStore();
      this.setToken('');
      this.setScopedRoles([]);
      await store.dispatch('socket/DISCONNECT');
      await store.dispatch('compute/FLUSH_COMPUTE');
      this.setProject('');
      await Promise.allSettled([
        store.dispatch('socket/CONNECT'),
        permissionsStore.fetchAuthMeta()
      ]);
      this.isSwitchingScope = false;
    },
    userSignedOut() {
      resetAbility();
      const modulesToClean = uniq([
        ...storeModulesGroups.default,
        ...storeModulesGroups.authorized,
        ...storeModulesGroups.compute,
        ...storeModulesGroups.monitoring,
        ...storeModulesGroups.settings,
        ...storeModulesGroups.storage,
        ...storeModulesGroups.infrastructure
      ]);
      modulesToClean.flatMap(name => findMutationsByName(name, 'FLUSH_STATE'))
        .forEach(mutationName => store.commit(mutationName));
    },
    startAuthReceiptTimer() {
      this.clearAuthReceiptTimer();
      this.authReceiptExpired = false;
      this.authReceiptTimestamp = Date.now();
      this.authReceiptTimerId = setTimeout(() => {
        this.handleAuthReceiptExpiration();
      }, AUTH_RECEIPT_EXPIRATION_TIME);
    },
    clearAuthReceiptTimer() {
      clearTimeout(this.authReceiptTimerId);
      this.authReceiptTimerId = undefined;
      this.authReceiptTimestamp = null;
    },
    handleAuthReceiptExpiration() {
      this.clearAuthReceiptTimer();
      this.authReceiptExpired = true;
      if (this.currentUser) {
        this.signOut();
      }
    }
  }
});

export default useAuthStore;
